import { useFetchWorkflowPreviousStep } from '@/hooks/use-workflow-previous-step';
import {
    WorkflowWorkflowType,
    WorkflowStepDefinition,
} from '@calm/evexia-web-sdk';
import { logger } from '@rhg/libs/logger';
import { useMutation } from '@rhg/libs/react-query';
import { FtueStoredStepName } from '@rhg/utils/workflows';
import { getCachedVideoId, storeCurrentStep } from '../../utils';

export interface GetPreviousFtueWorkflowStepParams {
    workflowDefnId: string;
    userSub?: string;
    onStepUpdate: (step: WorkflowStepDefinition | undefined) => void;
    skipCachedVideoStepBackward: (
        stepDefnId: string,
        workflowDefnId: string
    ) => Promise<void>;
}

// Mutation hook for getting previous FTUE workflow step
export function useGetPreviousFtueWorkflowStep() {
    const { fetchWorkflowPreviousStepAsync } = useFetchWorkflowPreviousStep();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: GetPreviousFtueWorkflowStepParams) => {
            const {
                workflowDefnId,
                userSub,
                onStepUpdate,
                skipCachedVideoStepBackward,
            } = params;

            try {
                const response = await fetchWorkflowPreviousStepAsync({
                    workflowType: WorkflowWorkflowType.WorkflowUserRegistration,
                    workflowDefnId,
                });
                const stepObj = response?.stepDefn;
                const previousStep = stepObj?.defnId;
                const hasCachedVideo = previousStep
                    ? !!(await getCachedVideoId(previousStep, userSub))
                    : false;

                if (previousStep && hasCachedVideo) {
                    await skipCachedVideoStepBackward(
                        previousStep,
                        workflowDefnId
                    );
                } else {
                    onStepUpdate(stepObj);
                    if (previousStep) {
                        storeCurrentStep(FtueStoredStepName, previousStep);
                    }
                }

                return response;
            } catch (error) {
                logger.error(
                    'Error updating previous workflow step:',
                    {
                        workflowDefnId,
                    },
                    error
                );
                throw error;
            }
        },
    });

    return {
        getPreviousFtueWorkflowStep: mutate,
        getPreviousFtueWorkflowStepAsync: mutateAsync,
        ...otherProps,
    };
}
